import { useEffect, useRef } from 'react';

export interface OpcoesAtalhosRegistro {
  /** Registro com alteracoes nao salvas (usado so para o ⌘Z dentro de campo de texto). */
  dirty: boolean;
  onSave?: () => void;
  onNew?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onUndo?: () => void;
  onList?: () => void;
  /** ⌘⌫ (Ctrl+Backspace). Deve abrir uma confirmacao: nunca exclui direto. */
  onDelete?: () => void;
}

/** Campo que usa o ⌘Z nativo do navegador (texto, area de texto, conteudo editavel). */
function ehCampoDeTexto(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false;
  if (alvo.isContentEditable) return true;
  if (alvo instanceof HTMLTextAreaElement) return true;
  if (alvo instanceof HTMLInputElement) {
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes(alvo.type);
  }
  return false;
}

/** Existe dialogo, alerta ou menu aberto: Esc e atalhos ficam com ele. */
function haSobreposicao(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]') !== null;
}

/**
 * Atalhos do registro: ⌘S salvar, ⌘N novo, ⌘[ anterior, ⌘] proximo, ⌘Z desfazer, ⌘⌫ excluir (com confirmacao),
 * Esc voltar a lista. Ctrl vale no lugar de ⌘ (Linux/Windows). Teclas repetidas (segurar) sao ignoradas.
 * Handlers ausentes nao disparam. ⌘Z em campo de texto segue nativo, salvo se o registro estiver alterado.
 */
export function useRecordShortcuts(opcoes: OpcoesAtalhosRegistro): void {
  const ref = useRef(opcoes);
  ref.current = opcoes;

  useEffect(() => {
    function aoPressionar(e: KeyboardEvent) {
      if (e.defaultPrevented || e.isComposing || e.repeat) return;
      const o = ref.current;

      if (e.key === 'Escape') {
        if (!o.onList || haSobreposicao()) return;
        e.preventDefault();
        o.onList();
        return;
      }

      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const tecla = e.key.toLowerCase();

      if (tecla === 's') {
        // Sempre bloqueia o "salvar pagina" do navegador, mesmo sem handler.
        e.preventDefault();
        o.onSave?.();
        return;
      }
      if (tecla === 'n' && o.onNew) {
        e.preventDefault();
        o.onNew();
        return;
      }
      if (tecla === '[' && o.onPrev) {
        e.preventDefault();
        o.onPrev();
        return;
      }
      if (tecla === ']' && o.onNext) {
        e.preventDefault();
        o.onNext();
        return;
      }
      if (e.key === 'Backspace' && o.onDelete) {
        // Em campo de texto, ⌘⌫ apaga a linha (nativo).
        if (ehCampoDeTexto(e.target) || haSobreposicao()) return;
        e.preventDefault();
        o.onDelete();
        return;
      }
      if (tecla === 'z' && !e.shiftKey && o.onUndo) {
        if (ehCampoDeTexto(e.target) && !o.dirty) return;
        e.preventDefault();
        o.onUndo();
      }
    }

    window.addEventListener('keydown', aoPressionar);
    return () => window.removeEventListener('keydown', aoPressionar);
  }, []);
}
